// Central SEO / GEO / AEO configuration for Brainmart MED CT.
// Single source of truth for canonical domain, hreflang locales, geo targeting
// (Argentina + provinces, Chile, Peru, Colombia, Puerto Rico, Mexico) and org data.

export const DOMAIN = "https://brainmart.com.ar";
import { isSpanishOnly, localizePath } from "./routes";

export const BRAND = {
  name: "Brainmart MED CT",
  legalName: "Brainmart",
  logo: `${DOMAIN}/brainmart-logo.png`,
  image: `${DOMAIN}/og/home.jpg`,
  sameAs: [
    "https://www.linkedin.com/company/brainmart-soft4health",
    "https://www.instagram.com/brainmart.tech/",
    "https://maps.app.goo.gl/vRAWMULdfGPdD4La9",
    "https://brainmart.com.ar",
  ],
  email: "info@brainmart.com.ar",
};

// hreflang locales. All Spanish-speaking LATAM markets share the same Spanish
// content, so every es-XX variant points to the Spanish version of each URL.
export const LOCALES = [
  { hreflang: "es", lang: "ES" },
  { hreflang: "es-AR", lang: "ES" },
  { hreflang: "es-CL", lang: "ES" },
  { hreflang: "es-PE", lang: "ES" },
  { hreflang: "es-CO", lang: "ES" },
  { hreflang: "es-PR", lang: "ES" },
  { hreflang: "es-MX", lang: "ES" },
  { hreflang: "en", lang: "EN" },
  { hreflang: "pt", lang: "PT" },
  { hreflang: "pt-BR", lang: "PT" },
];

// GEO targeting: markets served. NOTE: local regulator names are only mentioned
// where the user provided them (Argentina -> ANMAT). Other countries reference
// international standards only (FDA 21 CFR Part 11, ICH-GCP, ALCOA++, ISO 27001,
// EMA Anexo 11).
export const MARKETS = [
  { country: "Argentina", code: "AR", regulator: "ANMAT" },
  { country: "Chile", code: "CL" },
  { country: "Perú", code: "PE" },
  { country: "Colombia", code: "CO" },
  { country: "Puerto Rico", code: "PR" },
  { country: "México", code: "MX" },
];

// Main cities per country for local geo relevance (confirmed by user).
export const CITIES = {
  AR: ["Buenos Aires", "CABA", "Córdoba", "Rosario", "Mendoza", "La Plata", "Tucumán"],
  CL: ["Santiago", "Valparaíso", "Concepción", "Viña del Mar"],
  PE: ["Lima", "Arequipa", "Trujillo"],
  CO: ["Bogotá", "Medellín", "Cali", "Barranquilla"],
  PR: ["San Juan", "Bayamón", "Ponce"],
  MX: ["Ciudad de México", "Guadalajara", "Monterrey", "Puebla"],
};

export const buildUrl = (path = "/") => {
  if (path === "/") return `${DOMAIN}/`;
  return `${DOMAIN}${path.startsWith("/") ? path : `/${path}`}`;
};

// Organization / brand JSON-LD (used site-wide).
export const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${DOMAIN}/#organization`,
  name: BRAND.name,
  legalName: BRAND.legalName,
  url: `${DOMAIN}/`,
  logo: BRAND.logo,
  image: BRAND.image,
  email: BRAND.email,
  sameAs: BRAND.sameAs,
  areaServed: MARKETS.map((m) => ({ "@type": "Country", name: m.country })),
});

// SoftwareApplication JSON-LD describing the CTMS product.
export const softwareSchema = (t) => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "@id": `${DOMAIN}/#software`,
  name: "Brainmart MED CT",
  applicationCategory: "BusinessApplication",
  applicationSubCategory: "Clinical Trial Management System (Site CTMS & eSource)",
  operatingSystem: "Web",
  url: `${DOMAIN}/`,
  image: BRAND.image,
  description:
    "Site CTMS y eSource para centros de investigación clínica en Latinoamérica. Digitaliza HCE, farmacia, laboratorio, eISF, agenda y administración con trazabilidad completa lista para auditorías (FDA 21 CFR Part 11, GCP, ALCOA++).",
  provider: { "@id": `${DOMAIN}/#organization` },
  areaServed: MARKETS.map((m) => ({ "@type": "Country", name: m.country })),
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
    description: "Modelo DDA (Digital Data Agreement): sin costo directo para el site.",
  },
});

export const webSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${DOMAIN}/#website`,
  url: `${DOMAIN}/`,
  name: BRAND.name,
  alternateName: ["Brainmart", "Brainmart MED"],
  inLanguage: ["es", "en", "pt"],
  publisher: { "@id": `${DOMAIN}/#organization` },
});

// Secciones principales para que Google entienda la estructura (candidatas a sitelinks).
const SITE_NAV = [
  { path: "/modulos", ES: "Módulos", EN: "Modules", PT: "Módulos" },
  { path: "/solicitar-demo", ES: "Solicitar demo", EN: "Request a demo", PT: "Solicitar demo" },
  { path: "/casos", ES: "Casos de éxito", EN: "Case studies", PT: "Casos de sucesso" },
  { path: "/modulos/esource", ES: "eSource", EN: "eSource", PT: "eSource" },
  { path: "/faq", ES: "Preguntas frecuentes", EN: "FAQ", PT: "Perguntas frequentes" },
  { path: "/glosario", ES: "Glosario", EN: "Glossary", PT: "Glossário" },
  { path: "/blog", ES: "Blog", EN: "Blog", PT: "Blog" },
  { path: "/veeva", ES: "Veeva Product Partner", EN: "Veeva Product Partner", PT: "Veeva Product Partner" },
];

export const siteNavigationSchema = (lang = "ES") => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  itemListElement: SITE_NAV.map((it, i) => ({
    "@type": "SiteNavigationElement",
    position: i + 1,
    name: it[lang],
    url: buildUrl(isSpanishOnly(it.path) ? it.path : localizePath(it.path, lang)),
  })),
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: buildUrl(it.path),
  })),
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});
